import { T, useGT, Var } from 'gt-react';
import { SystemEntity, type TaskTypeViewSourceConfig } from '@erp/shared-types';
import { InvoiceDetailPanel } from '@/components/accounts-payable/invoice-detail/invoice-detail-slidein';
import { CashReceiptDetailView } from '@/components/accounts-receivable/cash-receipt-detail/cash-receipt-detail-view';
import { DocumentViewer } from '@/components/common/DocumentViewer';
import { InboundEmailViewer } from '@/components/email-viewer/InboundEmailViewer';
import { getEntityDisplayName } from '@/components/system-entities/utils';
import { Label } from 'src/components/ui/label';
import {
  resolveViewSource,
  type ResolvedViewSource,
  type ViewSourceKind,
} from './resolveTaskViewSource';

export function TaskTypeViewSource({
  kind,
  placementId,
  config,
  anchorType,
  anchorId,
  workingCopy,
}: {
  kind: ViewSourceKind;
  placementId: string;
  config: TaskTypeViewSourceConfig;
  anchorType: string | null;
  anchorId: string | null;
  workingCopy: Record<string, unknown>;
}) {
  const resolved = resolveViewSource({
    kind,
    source: config.source,
    anchorType,
    anchorId,
    workingCopy,
  });

  return (
    <div
      className="flex min-w-0 flex-col gap-1"
      data-testid={`task-view-source-${placementId}`}
    >
      {config.label ? <Label>{config.label}</Label> : null}
      {resolved.status === 'ready' ? (
        <ReadySource kind={kind} id={resolved.id} />
      ) : (
        <EmptySource kind={kind} resolved={resolved} />
      )}
    </div>
  );
}

function ReadySource({ kind, id }: { kind: ViewSourceKind; id: string }) {
  if (kind === 'apInvoice') {
    return (
      <div className="flex h-[70vh] min-h-0 flex-col overflow-hidden">
        <InvoiceDetailPanel invoiceId={id} />
      </div>
    );
  }
  if (kind === 'cashReceipt') {
    return (
      <div className="flex h-[70vh] min-h-0 flex-col overflow-hidden">
        <CashReceiptDetailView
          key={id}
          receiptId={id}
          hideActions
          hideTaskCard
        />
      </div>
    );
  }
  return (
    <div className="h-[70vh] min-h-0 overflow-auto">
      {kind === 'email' ? (
        <InboundEmailViewer inboundEmailId={id} />
      ) : (
        <DocumentViewer documentId={id} />
      )}
    </div>
  );
}

function EmptySource({
  kind,
  resolved,
}: {
  kind: ViewSourceKind;
  resolved: Extract<ResolvedViewSource, { status: 'empty' }>;
}) {
  const gt = useGT();
  const anchorName = anchorDisplayName(
    resolved.anchorType,
    gt('nothing', {
      context: 'task view empty state, the task has no anchor entity',
    }),
  );
  if (resolved.reason === 'missing-id') {
    return (
      <p className="text-sm text-muted-foreground">{missingIdCopy(kind, gt)}</p>
    );
  }
  return (
    <p className="text-sm text-muted-foreground">
      <WrongAnchor kind={kind} anchorName={anchorName} />
    </p>
  );
}

function missingIdCopy(
  kind: ViewSourceKind,
  gt: ReturnType<typeof useGT>,
): string {
  switch (kind) {
    case 'email':
      return gt('This field does not hold an email id.', {
        context: 'task view email placement with no id on the field',
      });
    case 'document':
      return gt('This field does not hold a document id.', {
        context: 'task view document placement with no id on the field',
      });
    case 'apInvoice':
      return gt('This field does not hold an AP invoice id.', {
        context: 'task view AP invoice placement with no id on the field',
      });
    case 'cashReceipt':
      return gt('This field does not hold a cash receipt id.', {
        context: 'task view cash receipt placement with no id on the field',
      });
  }
}

function WrongAnchor({
  kind,
  anchorName,
}: {
  kind: ViewSourceKind;
  anchorName: string;
}) {
  if (kind === 'email') {
    return (
      <T context="task view email placement when the task anchor is not an inbound email">
        This task is anchored to <Var>{anchorName}</Var>. An email viewer needs
        an inbound email.
      </T>
    );
  }
  if (kind === 'document') {
    return (
      <T context="task view document placement when the task anchor is not a document">
        This task is anchored to <Var>{anchorName}</Var>. A document viewer
        needs a document.
      </T>
    );
  }
  if (kind === 'cashReceipt') {
    return (
      <T context="task view cash receipt placement when the task anchor is not a cash receipt">
        This task is anchored to <Var>{anchorName}</Var>. A cash receipt viewer
        needs a cash receipt.
      </T>
    );
  }
  return (
    <T context="task view AP invoice placement when the task anchor is not an AP invoice">
      This task is anchored to <Var>{anchorName}</Var>. An AP invoice viewer
      needs an AP invoice.
    </T>
  );
}

function anchorDisplayName(anchorType: string | null, nothing: string): string {
  if (!anchorType) return nothing;
  if ((Object.values(SystemEntity) as string[]).includes(anchorType)) {
    return getEntityDisplayName(anchorType as SystemEntity);
  }
  return anchorType;
}
